import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ArrowLeft, User, Files, Coins, Calculator, PenNib, ListChecks, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TaxShell, StatusBadge, errToast } from "@/components/tax/shared";
import { ProfileStep } from "@/components/tax/ProfileStep";
import { DocumentsStep } from "@/components/tax/DocumentsStep";
import { IncomeStep } from "@/components/tax/IncomeStep";
import { ReviewStep } from "@/components/tax/ReviewStep";
import { AuthorizeStep } from "@/components/tax/AuthorizeStep";
import { StatusPanel } from "@/components/tax/StatusPanel";
import { useNavigate } from "react-router-dom";

const TABS = [
  { key: "profile", label: "About you", icon: User },
  { key: "documents", label: "Documents", icon: Files },
  { key: "income", label: "Income", icon: Coins },
  { key: "review", label: "Review", icon: Calculator },
  { key: "authorize", label: "Authorize", icon: PenNib },
  { key: "status", label: "Status", icon: ListChecks },
];

const defaultTab = (s) => (s === "awaiting_authorization" ? "authorize" : ["draft", "preparing", "documents_needed"].includes(s) ? "profile" : s === "ready_for_review" ? "review" : "status");

export default function TaxReturn() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const key = ["tax-return", id];
  const { data: meta } = useQuery({ queryKey: ["tax-meta"], queryFn: async () => (await api.get("/tax/meta")).data });
  const { data: ret, isLoading, error } = useQuery({
    queryKey: key, queryFn: async () => (await api.get(`/tax/returns/${id}`)).data,
    refetchInterval: (q) => (q.state.data?.documents?.some((d) => d.extraction_status === "pending") ? 3000 : false),
  });
  const [tab, setTab] = useState(null);
  useEffect(() => { if (ret && !tab) setTab(defaultTab(ret.status)); }, [ret, tab]);
  const refresh = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["tax-returns"] }); };
  const act = (fn, msg, next) => useMutation({ mutationFn: fn, onSuccess: () => { if (msg) toast.success(msg); refresh(); if (next) setTab(next); }, onError: errToast }); // eslint-disable-line react-hooks/rules-of-hooks

  const save = act((b) => api.put(`/tax/returns/${id}/profile`, b), "Saved securely", "documents");
  const calc = act(() => api.post(`/tax/returns/${id}/calculate`), "Estimate updated");
  const approve = act(() => api.post(`/tax/returns/${id}/approve-summary`), "Summary approved", "authorize");
  const authorize = act((b) => api.post(`/tax/returns/${id}/authorize`, b), "Signed and recorded", "status");
  const respond = act((m) => api.post(`/tax/returns/${id}/respond`, { message: m }), "Sent to your preparer");
  const withdraw = act(() => api.post(`/tax/returns/${id}/withdraw-consent`), "Consent withdrawn");
  const del = useMutation({ mutationFn: () => api.delete(`/tax/returns/${id}`), onSuccess: () => { toast.success("Return deleted"); qc.invalidateQueries({ queryKey: ["tax-returns"] }); nav("/tax"); }, onError: errToast });

  if (isLoading || !meta) return <TaxShell testId="tax-return-loading"><p className="text-slate-500">Loading…</p></TaxShell>;
  if (error) return <TaxShell testId="tax-return-error"><p className="text-red-600">Tax return not found.</p></TaxShell>;
  const locked = !!ret.sharing_consent && ret.status !== "action_required";
  const canAuthorize = ret.status === "awaiting_authorization";

  return (
    <TaxShell testId="tax-return-page">
      <Link to="/tax" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-red-600 mb-4" data-testid="tax-back-link"><ArrowLeft className="h-4 w-4" /> Tax Centre</Link>
      <div className="flex flex-wrap items-center gap-4 justify-between mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">{ret.tax_year} T1 return</h1>
          <p className="text-sm text-slate-500">Federal + Ontario · {ret.personal.legal_name || "Unnamed"}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={ret.status} testId="tax-return-status" />
          {!ret.sharing_consent && <Button size="sm" variant="ghost" data-testid="tax-delete-return-btn" onClick={() => window.confirm("Delete this return and permanently destroy its documents?") && del.mutate()}><Trash className="h-4 w-4 text-red-600" /></Button>}
        </div>
      </div>
      {locked && <div className="tax-card p-3 mb-4 text-sm text-slate-700" data-testid="tax-locked-banner">
        {["filed", "accepted"].includes(ret.status) ? "This return has been filed by your preparer and is now read-only." : ret.status === "rejected" ? "CRA rejected the electronic filing. Your preparer will follow up — editing stays locked unless they request information." : "Your return is with your preparer. Editing is locked unless they request information."}
      </div>}
      <div className="flex gap-1 overflow-x-auto mb-6 bg-white border border-slate-200 rounded-full p-1 w-fit max-w-full">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`tax-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${tab === t.key ? "bg-red-600 text-white" : "text-slate-600 hover:text-slate-900"}`}>
            <t.icon className="h-4 w-4" weight="bold" /> {t.label}
            {t.key === "authorize" && canAuthorize && <span className="h-2 w-2 rounded-full bg-amber-400" />}
          </button>
        ))}
      </div>
      {tab === "profile" && <ProfileStep ret={ret} onSave={save.mutate} saving={save.isPending} locked={locked} />}
      {tab === "documents" && <DocumentsStep ret={ret} meta={meta} locked={locked} refresh={refresh} />}
      {tab === "income" && <IncomeStep ret={ret} locked={locked} refresh={refresh} />}
      {tab === "review" && <ReviewStep ret={ret} locked={locked} onCalculate={calc.mutate} calculating={calc.isPending} onApprove={approve.mutate} approving={approve.isPending} />}
      {tab === "authorize" && (canAuthorize ? <AuthorizeStep key={ret.t183_request ? "t183" : "consent"} ret={ret} meta={meta} onSubmit={authorize.mutate} submitting={authorize.isPending} />
        : <p className="text-sm text-slate-500 tax-card p-5" data-testid="tax-authorize-unavailable">Authorization becomes available after you approve your summary on the Review tab, or when your preparer asks you to sign Form T183.</p>)}
      {tab === "status" && <StatusPanel ret={ret} onRespond={respond.mutate} responding={respond.isPending} onWithdraw={withdraw.mutate} />}
    </TaxShell>
  );
}
